import type { AnswerInputProps } from '../QuestionRenderer'

export function NumberInput({ question, value, onChange, onSubmit }: AnswerInputProps<number>) {
  const min = question.settings.numberMin
  const max = question.settings.numberMax
  const step = question.settings.numberStep ?? 1
  const unit = question.settings.numberUnit
  const decimals = Number(question.settings.numberDecimals ?? 0)
  const hasValue = typeof value === 'number' && Number.isFinite(value)
  const formattedValue = hasValue
    ? formatNumber(value, decimals, Boolean(question.settings.numberUseThousands))
    : null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-3">
        {question.settings.numberPrefix ? (
          <span
            className="pb-3 font-medium"
            style={{
              color: 'var(--form-answer,#64748B)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            }}
          >
            {question.settings.numberPrefix}
          </span>
        ) : null}
        <input
          type="number"
          value={value ?? ''}
          min={min}
          max={max}
          step={step}
          placeholder={question.settings.placeholder ?? '請輸入數字'}
          onChange={(event) => {
            const next = event.target.value.trim() === '' ? undefined : Number(event.target.value)
            if (next === undefined || Number.isFinite(next)) onChange(next as number)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSubmit()
          }}
          className="min-h-12 flex-1 border-0 border-b-2 bg-transparent px-0 py-3 text-2xl outline-none transition-colors focus:border-[var(--form-accent,#6366F1)]"
          style={{
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 34%, transparent)',
            color: 'var(--form-answer,#334155)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            fontSize: 'var(--form-input-size,1.25rem)',
            lineHeight: 'var(--form-line-height,1.55)',
          }}
        />
        {unit && (
          <span
            className="pb-3 font-medium"
            style={{
              color: 'var(--form-answer,#64748B)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            }}
          >
            {unit}
          </span>
        )}
        {question.settings.numberSuffix ? (
          <span
            className="pb-3 font-medium"
            style={{
              color: 'var(--form-answer,#64748B)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
            }}
          >
            {question.settings.numberSuffix}
          </span>
        ) : null}
      </div>
      {formattedValue ? (
        <div
          className="text-sm"
          style={{
            color: 'var(--form-answer,#64748B)',
            fontSize: 'var(--form-meta-size,0.9375rem)',
          }}
        >
          顯示為：
          {[question.settings.numberPrefix, formattedValue, unit, question.settings.numberSuffix]
            .filter(Boolean)
            .join(' ')}
        </div>
      ) : null}
      <div
        className="flex flex-wrap gap-2 text-sm"
        style={{
          color: 'var(--form-answer,#64748B)',
          fontSize: 'var(--form-meta-size,0.9375rem)',
        }}
      >
        {min !== undefined && <span>最小 {min}</span>}
        {max !== undefined && <span>最大 {max}</span>}
        {step !== undefined && <span>間距 {step}</span>}
        {decimals > 0 && <span>小數 {decimals} 位</span>}
      </div>
      {question.settings.errorMessage ? (
        <div className="text-sm text-rose-500">{question.settings.errorMessage}</div>
      ) : null}
    </div>
  )
}

function formatNumber(value: number, decimals: number, useThousands: boolean) {
  if (!useThousands) return value.toFixed(decimals)
  return new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}
